import type { ProposalOrderStatus } from '@moment/horizon-data-access/proposal'
import type { BondType } from '@moment/trading-types/reference-data'
import type React from 'react'
import type { TradeBlotterOrderStatus } from '@/utils/trade-blotter/compute-order-status'

import ClickToCopy from '@/components/atomic/click-to-copy'
import { BodyDefault, BodySubtext, FontWeight } from '@/components/atomic/text'
import { BondTypeBadge } from '@/components/molecules/badges/bond-type'
import { OrderStatusBadge } from '@/components/molecules/badges/order-status'

interface BondHeaderDetailsProps {
  bondTitle: string
  id: string
  bondType?: BondType
  orderStatus?: TradeBlotterOrderStatus | ProposalOrderStatus
  isExecuting?: boolean
}

// Renders the appropriate badge based on execution state and bond type
function StatusBadge({
  isExecuting,
  orderStatus,
  bondType,
  className,
}: {
  isExecuting?: boolean
  orderStatus?: TradeBlotterOrderStatus | ProposalOrderStatus
  bondType?: BondType
  className?: string
}) {
  if (isExecuting && orderStatus) {
    return <OrderStatusBadge status={orderStatus} className={className} />
  }

  if (!isExecuting && bondType) {
    return <BondTypeBadge bondType={bondType} className={className} />
  }

  return null
}

// Main component that combines bond details and status information
const BondHeaderDetails: React.FC<BondHeaderDetailsProps> = ({
  bondTitle,
  id,
  bondType,
  orderStatus,
  isExecuting,
}) => {
  return (
    <div className="flex items-start justify-between  gap-2.5">
      <div className="flex items-start gap-2.5 flex-grow">
        <div className="flex flex-col gap-0.75">
          <BodyDefault
            className="text-balance mt-0.5 leading-[135%]"
            fontWeight={FontWeight.SemiBold}
          >
            {bondTitle}
          </BodyDefault>
          <ClickToCopy value={id}>
            <BodySubtext className="text-secondary">{id}</BodySubtext>
          </ClickToCopy>
        </div>
        <StatusBadge
          isExecuting={isExecuting}
          orderStatus={orderStatus}
          bondType={bondType}
        />
      </div>
    </div>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default BondHeaderDetails
